import type { Platform } from "@/types/ExchangeAuthHealth";
import type { App } from "@/types/App";

// LSP-16585: The Exchange "failing-auths" endpoint can return platforms that the
// current organization is not actually connected to (e.g. stale refresh-failure
// records for an app that was never installed or has since been disconnected).
// Showing the reconnect banner for those platforms is misleading — the user sees
// "Some Apps need to be reconnected" while the Integrations tab shows nothing
// connected. We only want to surface a platform when it maps to an app the org
// has actually installed, so the banner state matches the Integrations UI.
const filterConnectedFailingPlatforms = (failingPlatforms: Platform[] | null | undefined, apps: App[] | null | undefined): Platform[] => {
	if (!failingPlatforms || failingPlatforms.length === 0) {
		return [];
	}
	const installedCodeNames = new Set(
		(apps || []).filter((app) => app && app.isInstalled && app.codeName).map((app) => app.codeName.toLowerCase())
	);
	if (installedCodeNames.size === 0) {
		return [];
	}
	return failingPlatforms.filter(
		(platform) => platform && platform.platformCodeName && installedCodeNames.has(platform.platformCodeName.toLowerCase())
	);
};

// LSP-16585 (BugBot): decide which platforms drive the reconnect banner. When the
// installed-apps list could not be loaded (`apps == null` — the fetch rejected or
// returned a non-OK response), we CANNOT reconcile, so we fall back to the raw failing
// platforms rather than HIDE a genuine reconnect need. Only an actually-loaded apps list
// (even an empty array, meaning "nothing installed") is allowed to suppress the banner.
const resolveReconnectBannerPlatforms = (
	rawFailingPlatforms: Platform[] | null | undefined,
	apps: App[] | null | undefined
): Platform[] => {
	if (apps == null) {
		return rawFailingPlatforms ?? [];
	}
	return filterConnectedFailingPlatforms(rawFailingPlatforms, apps);
};

export { filterConnectedFailingPlatforms, resolveReconnectBannerPlatforms };
